import algoliasearch from 'algoliasearch/lite';
import { ProductImage, ProductListCardData } from '../product/product-types';
import { ImageWordpress } from '../../types/shared';
import { ProductIconName } from '../product/product-icon-tab/product-icon-tab-types';
import { removeTrailingSlash } from '../../tools/remove-trailing-slash';

/* Query-string contract of the search page. `s` predates this rework and is also what the
   WebSite schema's SearchAction advertises, so it stays. */
export const SEARCH_QUERY_PARAM = 's';
export const SEARCH_TYPE_PARAM = 'type';
export const SEARCH_PAGE_PARAM = 'page';

/* `type` on the Algolia records, written by content-search-sync (`KnownType`). */
export type SearchHitType = 'page' | 'product' | 'blog-and-news' | 'event' | 'video-item' | 'document';

export type SearchResultFilter = 'all' | SearchHitType;

/* Tab order on the results page. Videos sit last: most sites have none. */
export const SEARCH_RESULT_FILTERS: SearchResultFilter[] = ['all', 'page', 'product', 'blog-and-news', 'event', 'document', 'video-item'];

export const isSearchResultFilter = (value: unknown): value is SearchResultFilter =>
    typeof value === 'string' && (SEARCH_RESULT_FILTERS as string[]).includes(value);

/* One Algolia record. Only the fields the UI reads; see content-search-sync `data-parser.ts`
   for everything that is indexed. Records indexed before the card fields existed have no
   image/description, so both stay optional. */
export type SearchHit = {
    objectID: string;
    url: string;
    type: SearchHitType;
    postTitle: string;
    tags?: string[];
    description?: string;
    image?: {
        url: string;
        alt?: string;
    };
    icon?: ProductIconName;
    permalink?: string;
    isHealthcareProfessional?: boolean;
    isVisibleForPublic?: boolean;
    hideForLoggedOutUsersOnCareSite?: boolean;
};

type SearchSettings = {
    searchAppId: string;
    searchApiClientKey: string;
};

type SearchSite = {
    siteId: string;
    target: string;
};

/* One index per site and target. The key is the public search-only key, so the browser
   talks to Algolia directly. */
export const createSearchClient = (settings: SearchSettings, site: SearchSite) => {
    const client = algoliasearch(settings.searchAppId, settings.searchApiClientKey);

    return {
        client,
        indexName: `${site.siteId}-${site.target}`,
    };
};

/* Empty string is the start page in the index. */
export const hitHref = (hit: SearchHit) => (hit.url === '' ? '/' : hit.url);

/* Documents are files under /downloads — they open in a new tab like the old result list did. */
export const hitOpensInNewTab = (hit: SearchHit) => hit.type === 'document';

export const buildSearchPageUrl = (searchPageUrl: string, query: string) => {
    const base = removeTrailingSlash(searchPageUrl);
    const trimmed = query.trim();

    if (!trimmed) {
        return base;
    }

    return `${base}?${SEARCH_QUERY_PARAM}=${encodeURIComponent(trimmed)}`;
};

/* The product cards on the search page render the same `ProductCard` as the listing, which
   expects the listing's data shape. */
export const mapHitToProductCard = (hit: SearchHit): ProductListCardData => ({
    url: hitHref(hit),
    permalink: hit.permalink || hit.url,
    'post_title': hit.postTitle,
    shortDescription: hit.description,
    icon: hit.icon,
    isHealthcareProfessional: hit.isHealthcareProfessional,
    isVisibleForPublic: hit.isVisibleForPublic,
    images: hit.image ? [toProductImage(hit.image)] : [],
});

/* The card only reads url and alt; the rest of ImageWordpress is not indexed. */
const toProductImage = (image: NonNullable<SearchHit['image']>): ProductImage => ({
    file: { url: image.url, alt: image.alt } as ImageWordpress,
    isFeatured: true,
});
